import { ProductCarousel } from 'components/Carousel';
import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { LayoutContainer } from 'components/Layout';
import { getValidSitecoreLink } from 'components/SitecoreLink';
import Text from 'components/Text';
import { useIsEditing, useRouteFields } from 'hooks';
import type { ProductCard as ProductCardModel } from 'models/productCard';
import type { Field, SitecoreBlockProps, SitecoreLink } from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { is } from 'utils/helpers';

interface Props extends SitecoreBlockProps {
	fields: {
		allowQuickBuy?: Field<boolean>;
		datasourceName?: string;
		heading: Field<string>;
		linksHeading?: Field<string>;
		primaryLink?: Field<SitecoreLink>;
		products: ProductCardModel[];
		quaternaryLink?: Field<SitecoreLink>;
		secondaryLink?: Field<SitecoreLink>;
		tertiaryLink?: Field<SitecoreLink>;
	};
}

export default function ProductCarouselBlock({ fields, params }: Props) {
	const isEditing = useIsEditing();
	const routeFields = useRouteFields();
	const hasPageTheme = Boolean(routeFields?.pageTheme?.value);

	const {
		allowQuickBuy,
		datasourceName,
		heading,
		primaryLink,
		products,
		quaternaryLink,
		secondaryLink,
		tertiaryLink,
	} = fields;
	const hasProducts = products.length > 0;

	if (
		(isEditing && !heading?.value && !hasProducts) ||
		(!isEditing && !hasProducts)
	) {
		return (
			<ComponentPlaceholder
				componentName="ProductCarousel"
				description="Requires products"
				className={getEditorMargin(params)}
			/>
		);
	}

	const linksHeading = fields.linksHeading?.value;
	const links = [primaryLink, secondaryLink, tertiaryLink, quaternaryLink]
		.map((link) => getValidSitecoreLink(link?.value))
		.filter(is.truthy);

	const carousel = (
		<ProductCarousel
			products={products}
			gtmItemListId="block"
			gtmItemListName={`Block: ${datasourceName ?? 'ProductCarouselUnknown'}`}
			gutter={hasPageTheme ? 'md' : 'focus-outline'}
			pullGutter={!hasPageTheme}
			microContentLinks={links}
			microContentLinksTitle={linksHeading}
			showAddToCartButton={allowQuickBuy?.value}
		/>
	);

	return (
		<LayoutContainer
			id={params?.anchor}
			outerClassName={getEditorMargin(params)}
		>
			<Text as={params?.heading ?? 'h2'} field={heading} className="mb-8" />
			{hasPageTheme && (
				<div className="rounded-md bg-base pt-2 pb-r-md text-base max-sm:-mr-4 max-sm:rounded-r-none">
					{carousel}
				</div>
			)}
			{!hasPageTheme && carousel}
		</LayoutContainer>
	);
}
ProductCarouselBlock.displayName = 'ProductCarouselBlock';
